import React, { useState } from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import { useLang } from '@/contexts/LanguageContext';
import { useCart } from '@/contexts/CartContext';
import { getProduct, PRODUCTS as DATA } from '@/data/products';
import { WHATSAPP_LINK } from '@/data/brand';
import { PRODUCTS } from '@/constants/testIds';
import ProductCard from '@/components/ProductCard';
import { ArrowLeft, Minus, Plus, ShoppingBag } from 'lucide-react';

export default function ProductDetail() {
  const { slug } = useParams();
  const { t, lang } = useLang();
  const { add } = useCart();
  const product = getProduct(slug);
  const [selectedShade, setSelectedShade] = useState(null);
  const [qty, setQty] = useState(1);
  const [activeImage, setActiveImage] = useState(0);
  if (!product) return <Navigate to="/productos" replace />;

  const gallery = product.gallery && product.gallery.length > 0 ? product.gallery : [product.image];
  const currentImage = gallery[activeImage] || product.image;

  const related = DATA.filter((p) => p.slug !== slug).slice(0, 3);
  const waMsg = lang === 'es'
    ? `Hola Jelly! Me interesa ${product.name.es}. ¿Pueden darme más info?`
    : `Hi Jelly! I'm interested in ${product.name.en}. Can you tell me more?`;

  return (
    <div className="bg-[#FFFBFB]" data-testid={PRODUCTS.detail}>
      <section className="px-6 md:px-12 lg:px-24 pt-10 pb-20 md:pb-24">
        <div className="max-w-7xl mx-auto">
          <Link
            to="/productos"
            className="inline-flex items-center gap-2 text-xs font-bold tracking-widest uppercase text-[#574A66] hover:text-[#FF7597]"
          >
            <ArrowLeft className="w-4 h-4" /> {t('products.back')}
          </Link>

          <div className="mt-8 grid lg:grid-cols-2 gap-10 lg:gap-20 items-center">
            <div>
              <div
                className="relative rounded-[2.5rem] overflow-hidden aspect-square lg:aspect-[4/5]"
                style={{ backgroundColor: product.color }}
              >
                <div className="absolute inset-0 grain" />
                <img
                  src={currentImage}
                  alt={product.name[lang]}
                  className="absolute inset-0 w-full h-full object-contain p-8 transition-opacity duration-300"
                  key={currentImage}
                />
                <span
                  className="absolute top-5 left-5 text-[10px] font-bold tracking-[0.25em] uppercase px-3 py-1 rounded-full bg-white/80 backdrop-blur"
                  style={{ color: product.accent }}
                >
                  Jelly Drop 01
                </span>
              </div>

              {gallery.length > 1 && (
                <div className="mt-4 flex gap-3">
                  {gallery.map((img, i) => (
                    <button
                      key={img}
                      type="button"
                      onClick={() => setActiveImage(i)}
                      aria-label={`View image ${i + 1}`}
                      data-testid={`product-thumb-${i}`}
                      className={`relative w-20 h-20 md:w-24 md:h-24 rounded-2xl overflow-hidden border-2 transition-all ${
                        activeImage === i
                          ? 'border-[#FF2D9C] scale-105 shadow-[0_8px_24px_rgba(255,45,156,0.25)]'
                          : 'border-[#A99BE0]/20 hover:border-[#A99BE0]/60 opacity-75 hover:opacity-100'
                      }`}
                      style={{ backgroundColor: product.color }}
                    >
                      <img
                        src={img}
                        alt={`${product.name[lang]} ${i + 1}`}
                        className="absolute inset-0 w-full h-full object-contain p-2"
                      />
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div>
              <p className="text-[10px] md:text-xs font-bold tracking-[0.3em] uppercase" style={{ color: product.accent }}>
                {product.tagline[lang]}
              </p>
              <h1 className="font-display mt-3 text-5xl md:text-6xl font-black tracking-tighter text-[#1F1A24] text-balance">
                {product.name[lang]}
              </h1>
              <p className="mt-3 font-display text-3xl text-[#FF7597] font-bold">{product.price}</p>

              <p className="mt-6 text-base text-[#574A66] leading-relaxed">{product.description[lang]}</p>

              <div className="mt-8">
                <p className="text-[10px] font-bold tracking-[0.25em] uppercase text-[#574A66] mb-3">
                  {t('products.shades')}
                </p>
                <div className="flex flex-wrap gap-2">
                  {product.shades.map((s, i) => {
                    const isSelected = selectedShade === i;
                    return (
                      <button
                        key={s.label + i}
                        type="button"
                        onClick={() => setSelectedShade(isSelected ? null : i)}
                        className={`text-xs px-4 py-2 rounded-full border transition-all ${
                          isSelected
                            ? 'bg-[#FF2D9C] text-white border-[#FF2D9C] scale-105'
                            : 'border-[#FF7597]/30 bg-white text-[#1F1A24] hover:border-[#FF2D9C] hover:text-[#FF2D9C]'
                        }`}
                      >
                        {s.label}
                      </button>
                    );
                  })}
                </div>
                {selectedShade !== null && product.shades[selectedShade]?.ref && (
                  <div className="mt-3 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#FAEFC2] border border-[#F4DD8C]">
                    <span className="text-[10px] font-bold tracking-[0.2em] uppercase text-[#574A66]">
                      {lang === 'es' ? 'Referencia' : 'Reference'}
                    </span>
                    <span className="font-display font-black text-sm text-[#1F1A24] tracking-tight">
                      {product.shades[selectedShade].ref}
                    </span>
                  </div>
                )}
              </div>

              <div className="mt-8 grid sm:grid-cols-2 gap-4">
                <div className="rounded-3xl bg-white border border-[#FF7597]/10 p-5">
                  <p className="text-[10px] font-bold tracking-[0.25em] uppercase text-[#FF7597] mb-2">
                    {t('products.key_ingredients')}
                  </p>
                  <ul className="text-sm text-[#1F1A24] space-y-1">
                    {product.ingredients[lang].map((i) => (
                      <li key={i}>• {i}</li>
                    ))}
                  </ul>
                </div>
                <div className="rounded-3xl bg-white border border-[#FF7597]/10 p-5">
                  <p className="text-[10px] font-bold tracking-[0.25em] uppercase text-[#FF7597] mb-2">
                    {t('products.how_to_use')}
                  </p>
                  <p className="text-sm text-[#1F1A24] leading-relaxed">{product.howTo[lang]}</p>
                </div>
              </div>

              <div className="mt-10 flex flex-col sm:flex-row gap-3 items-stretch sm:items-center">
                <div className="inline-flex items-center gap-1 rounded-full border border-[#FF7597]/30 bg-white px-1.5 py-1 self-start sm:self-auto">
                  <button
                    onClick={() => setQty(Math.max(1, qty - 1))}
                    className="h-9 w-9 inline-flex items-center justify-center rounded-full hover:text-[#FF2D9C]"
                    aria-label="Restar"
                  >
                    <Minus className="w-4 h-4" />
                  </button>
                  <span className="text-base font-bold w-8 text-center">{qty}</span>
                  <button
                    onClick={() => setQty(qty + 1)}
                    className="h-9 w-9 inline-flex items-center justify-center rounded-full hover:text-[#FF2D9C]"
                    aria-label="Sumar"
                  >
                    <Plus className="w-4 h-4" />
                  </button>
                </div>

                <button
                  data-testid="product-detail-add-cart"
                  onClick={() => add(product.slug, selectedShade ?? -1, qty)}
                  className="inline-flex items-center justify-center gap-2 px-7 py-4 rounded-full bg-[#FF2D9C] text-white text-sm font-bold tracking-widest uppercase hover:bg-[#FF1F8A] hover:scale-[1.02] transition-all shadow-[0_12px_40px_rgba(255,45,156,0.35)]"
                >
                  <ShoppingBag className="w-4 h-4" />
                  {lang === 'es' ? 'Añadir al carrito' : 'Add to cart'}
                </button>

                <a
                  data-testid={PRODUCTS.detailWhatsapp}
                  href={WHATSAPP_LINK(waMsg)}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center justify-center gap-2 px-6 py-4 rounded-full bg-white text-[#1F1A24] text-sm font-bold tracking-widest uppercase border border-[#1F1A24]/15 hover:border-[#FF2D9C] hover:text-[#FF2D9C] transition-all"
                >
                  {t('products.askWhatsapp')}
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {related.length > 0 && (
        <section className="px-6 md:px-12 lg:px-24 pb-24">
          <div className="max-w-7xl mx-auto">
            <p className="text-[10px] md:text-xs font-bold tracking-[0.3em] uppercase text-[#FF7597] mb-6">
              {lang === 'es' ? 'TAMBIÉN TE PUEDE GUSTAR' : 'YOU MIGHT ALSO LIKE'}
            </p>
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 md:gap-8">
              {related.map((p, i) => (
                <ProductCard key={p.slug} product={p} index={i} />
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
